
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Globe, 
  BarChart2, 
  Palette, 
  Settings,
  FileText,
  BookOpen,
  Tag,
  X,
  MessageSquare,
  PieChart,
  Coins
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/use-theme';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePermissions } from '@/hooks/use-permissions';

interface AdminSidebarProps {
  onClose?: () => void;
}

const AdminSidebar = ({ onClose }: AdminSidebarProps) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { isSuperAdmin } = usePermissions();

  const getLinkClass = ({ isActive }: { isActive: boolean }) => {
    return `flex items-center space-x-2 px-4 py-2.5 rounded-md transition-colors ${
      isActive 
        ? 'text-white bg-nation-blue dark:bg-nation-blue-dark' 
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`;
  };

  return (
    <div className={`${isMobile ? "h-full" : "hidden w-64"} flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 md:block`}>
      {/* Mobile close button */}
      {isMobile && onClose && (
        <div className="flex justify-end mb-2">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={18} />
          </Button>
        </div>
      )}
      
      <Link to="/" className="flex items-center mb-6">
        <img
          src={theme === 'dark' ? '/img/nationcard-dark.png' : '/img/nationcard-light.png'}
          alt="NationCard™ Logo"
          className="h-8"
        />
      </Link>
      
      <nav className="flex flex-col space-y-1">
        <NavLink to="/admin/dashboard" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <LayoutDashboard size={18} />
          <span>Dashboard</span>
        </NavLink>
        
        <NavLink to="/admin/users" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <Users size={18} />
          <span>Users</span>
        </NavLink>
        
        <NavLink to="/admin/countries" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <Globe size={18} />
          <span>Countries</span>
        </NavLink>
        
        <NavLink to="/admin/insights" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <BarChart2 size={18} />
          <span>Insights</span>
        </NavLink>
        
        <NavLink to="/admin/polls" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <FileText size={18} />
          <span>Polls</span>
        </NavLink>

        {/* Super Admin Only Menu Items */}
        {isSuperAdmin() && (
          <>
            <NavLink to="/admin/poll-management" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
              <PieChart size={18} />
              <span>Poll Management</span>
            </NavLink>

            <NavLink to="/admin/categories" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
              <Tag size={18} />
              <span>Categories</span>
            </NavLink>

            <NavLink to="/admin/points" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
              <Coins size={18} />
              <span>Points Management</span>
            </NavLink>
          </>
        )}
        
        <NavLink to="/admin/civic-education" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <BookOpen size={18} />
          <span>Civic Education</span>
        </NavLink>
        
        <NavLink to="/admin/sitemap" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <Globe size={18} />
          <span>Sitemap</span>
        </NavLink>
        
        {/* Super Admin Only Menu Items */}
        {isSuperAdmin() && (
          <>
            <NavLink to="/admin/appearance" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
              <Palette size={18} />
              <span>Appearance</span>
            </NavLink>
            
            <NavLink to="/admin/settings" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
              <Settings size={18} />
              <span>Settings</span>
            </NavLink>
          </>
        )}
        
        <NavLink to="/admin/identity-docs" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
          <Users size={18} />
          <span>Identity Documents</span>
        </NavLink>
        
        {/* Super Admin Only Menu Items */}
        {isSuperAdmin() && (
          <NavLink to="/comments-feedback" className={getLinkClass} onClick={isMobile ? onClose : undefined}>
            <MessageSquare size={18} />
            <span>Comments & Votes</span>
          </NavLink>
        )}
      </nav>
      
      <div className="mt-auto pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
        <Button variant="outline" className="w-full" asChild>
          <NavLink to="/" onClick={isMobile ? onClose : undefined}>View Website</NavLink>
        </Button>
      </div>
    </div>
  );
};

export default AdminSidebar;
